/**
 * Prepares a floorplan AI-render snapshot for GraphQL upload.
 * The live 3D capture can be a multi-megapixel PNG that exceeds proxy body
 * limits (HTTP 413) before the mutation runs. Recraft also rejects sides
 * longer than 4096px, so we match that cap client-side.
 */
export const AI_RENDER_MAX_INPUT_DIMENSION = 4096;
export const AI_RENDER_JPEG_QUALITY = 0.85;

export function fitWithinMaxDimension(
  width: number,
  height: number,
  maxDimension: number = AI_RENDER_MAX_INPUT_DIMENSION,
): { width: number; height: number } {
  const safeWidth = Math.max(1, Math.floor(width));
  const safeHeight = Math.max(1, Math.floor(height));
  const longest = Math.max(safeWidth, safeHeight);
  if (longest <= maxDimension) {
    return { width: safeWidth, height: safeHeight };
  }
  const scale = maxDimension / longest;
  return {
    width: Math.max(1, Math.floor(safeWidth * scale)),
    height: Math.max(1, Math.floor(safeHeight * scale)),
  };
}

export function isContentTooLargeError(error: unknown): boolean {
  const statusCode = getNetworkStatusCode(error);
  if (statusCode === 413) return true;
  return /status code 413|content too large|payload too large|request entity too large/i.test(
    getErrorMessage(error),
  );
}

/**
 * Downscale and JPEG-compress a canvas data URL or captured File so the
 * GraphQL multipart body stays under typical proxy limits.
 */
export async function prepareAiRenderSnapshotFile(
  source: string | Blob,
  filename = `floorplan_render_${Date.now()}.jpg`,
): Promise<File> {
  const objectUrl =
    typeof source === "string" ? source : URL.createObjectURL(source);
  try {
    const image = await loadImage(objectUrl);
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    const { width, height } = fitWithinMaxDimension(sourceWidth, sourceHeight);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Could not prepare the AI render snapshot.");
    }
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    const blob = await canvasToJpegBlob(canvas, AI_RENDER_JPEG_QUALITY);
    const jpegName = filename.replace(/\.png$/i, ".jpg");
    return new File([blob], jpegName, { type: "image/jpeg" });
  } finally {
    if (typeof source !== "string") {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error && "message" in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error ?? "");
}

function getNetworkStatusCode(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const withStatus = error as {
    statusCode?: number;
    networkError?: { statusCode?: number };
  };
  if (typeof withStatus.statusCode === "number") {
    return withStatus.statusCode;
  }
  return typeof withStatus.networkError?.statusCode === "number"
    ? withStatus.networkError.statusCode
    : undefined;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not decode the 3D snapshot."));
    image.src = src;
  });
}

function canvasToJpegBlob(
  canvas: HTMLCanvasElement,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (typeof canvas.toBlob === "function") {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
            return;
          }
          resolve(dataUrlToBlob(canvas.toDataURL("image/jpeg", quality)));
        },
        "image/jpeg",
        quality,
      );
      return;
    }
    try {
      resolve(dataUrlToBlob(canvas.toDataURL("image/jpeg", quality)));
    } catch (error) {
      reject(error);
    }
  });
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [metadata, data] = dataUrl.split(",");
  const mime = metadata.match(/data:(.*?);base64/)?.[1] ?? "image/jpeg";
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mime });
}
